import { Skeleton } from "@babackapp/ui/components/skeleton";
import { cn } from "@babackapp/ui/lib/utils";
import { useFieldArray, useFormContext } from "react-hook-form";
import ExampleFileInput from "./example-file-input";
import ImportPropertySelect from "./import-property-select";
import {
  ShopImportFormValues,
  ShopImportMappingColumnType,
} from "./import-settings-form-types";

type ImportMappingsFormProps = {
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
};

export default function ImportMappingsForm({
  isLoading,
  setIsLoading,
}: ImportMappingsFormProps) {
  const { control } = useFormContext<ShopImportFormValues>();

  const mapping = useFieldArray({
    control,
    name: "mapping",
  });

  if (isLoading) {
    return <ImportMappingsFormLoading />;
  }

  if (!mapping.fields.length) {
    return <ImportMappingsFormDisabled onLoad={setIsLoading} />;
  }

  const onChange = (fieldId: number, mapTo: ShopImportMappingColumnType) => {
    mapping.update(fieldId, { ...mapping.fields[fieldId], mapTo });
  };

  return (
    <>
      <div className="rounded-lg max-h-[600px] overflow-auto flex flex-col gap-2 bg-muted/50 border-border border p-2">
        {mapping.fields.map((field) => (
          <ImportMappingsRow
            key={field.id}
            title={field.fieldName}
            subtitle={field.exampleValue}
            index={field.fieldIndex}
            mapTo={field.mapTo}
            updateMapTo={(mapTo) => onChange(field.fieldIndex, mapTo)}
          />
        ))}
      </div>
    </>
  );
}

function ImportMappingsFormLoading() {
  return (
    <div className="flex flex-col gap-2">
      <ImportMappingRowSkeleton />
      <ImportMappingRowSkeleton />
      <ImportMappingRowSkeleton />
      <ImportMappingRowSkeleton />
    </div>
  );
}

type ImportMappingsFormDisabledProps = {
  onLoad?: (b: boolean) => void;
};

function ImportMappingsFormDisabled({
  onLoad,
}: ImportMappingsFormDisabledProps) {
  return (
    <>
      <div className="rounded-lg w-full relative">
        <div className="blur-sm">
          <ImportMappingsFormLoading />
        </div>
        <div className="absolute px-4 flex items-center justify-center inset-0">
          <ExampleFileInput onLoad={onLoad} />
        </div>
      </div>
    </>
  );
}

function ImportMappingRowSkeleton() {
  return (
    <div className="border-border/50 border rounded-md px-4 py-2 flex items-center gap-4">
      <Skeleton className="h-4 w-4" />
      <div className="flex flex-col">
        <Skeleton className="h-4 w-16 mb-1" />
        <Skeleton className="h-3 w-12" />
      </div>
      <Skeleton className="h-4 w-4 mx-auto" />
      <Skeleton className="h-8 w-[150px]" />
    </div>
  );
}

type ImportMappingRowProps = {
  title: string;
  subtitle: string;
  index: number;
  mapTo: ShopImportMappingColumnType;
  updateMapTo: (mapTo: ShopImportMappingColumnType) => void;
};

function ImportMappingsRow({
  title,
  subtitle,
  mapTo,
  updateMapTo,
}: ImportMappingRowProps) {
  return (
    <div
      className={cn(
        "bg-background border-border border rounded-md px-4 py-2 flex items-center gap-4 opacity-50",
        mapTo && "opacity-100",
      )}
    >
      <div className="flex flex-1 flex-col">
        <span className="text-sm">{title}</span>
        <span className="text-xs text-muted-foreground">({subtitle})</span>
      </div>
      <span className="text-muted-foreground text-sm flex-1 text-center">
        ➔
      </span>
      <ImportPropertySelect value={mapTo} onChange={updateMapTo} />
    </div>
  );
}
